enum MutationType {
  CHILD_LIST = 'childList',
  ATTRIBUTES = 'attributes',
  BOTH = 'both',
}

type MutationPromiseType = string | Element;

interface MutationObservations {
  insertedInDOM: () => Promise<Element>;
  insertedInElement: (parentElement: Element) => Promise<Element>;
  attributesChanged: () => Promise<string>;
}

function createObservationPromise<T extends MutationPromiseType>(
  selector: string,
  parentElement: Element = document.body,
  mutationType: MutationType = MutationType.BOTH
): Promise<T> {
  return new Promise((resolve) => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const { addedNodes } = mutation;
        const { attributeName } = mutation;

        if (mutationType === MutationType.ATTRIBUTES && attributeName) {
          resolve(attributeName as T);
        } else {
          addedNodes.forEach((node) => {
            if (node instanceof Element && node.matches(selector)) {
              observer.disconnect();
              resolve(node as T);
            }
          });
        }
      });
    });

    observer.observe(parentElement, {
      attributes: mutationType === 'attributes' || mutationType === 'both',
      childList: mutationType === 'childList' || mutationType === 'both',
      subtree:
        (mutationType === 'childList' || mutationType === 'both') &&
        parentElement === document.body,
    });
  });
}

function insertedInDOM<T extends MutationPromiseType>(
  selector: string,
  parentElement: Element = document.body
): Promise<T> {
  return createObservationPromise(
    selector,
    parentElement,
    MutationType.CHILD_LIST
  );
}

function attributesChanged<T extends MutationPromiseType>(
  selector: string
): Promise<T> {
  return createObservationPromise(
    selector,
    document.body,
    MutationType.ATTRIBUTES
  );
}

export function when(selector: string): MutationObservations {
  return {
    insertedInDOM: () => insertedInDOM(selector),
    insertedInElement: (parent: Element) => insertedInDOM(selector, parent),
    attributesChanged: () => attributesChanged(selector),
  };
}
